import React, { useEffect, useState } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { metodoPago, ORDER_STATES, tiposDeDocumento } from '../../assets/data/arrays'
import { Input } from '../ui'
import { FormField } from '../ui/Form'
import type { DevolucionOrderDetail as DevolucionOrderDetailType } from '../../types/devoluciones'
import { getDevolucionCustomerName } from '../../utils/devoluciones-utils'

interface DevolucionOrderDetailProps {
  order?: DevolucionOrderDetailType | null
  children?: React.ReactNode
}

function getLabel<T extends { value: string; label: string }>(
  rows: T[],
  value?: string
): string {
  if (!value) return ''
  return rows.find((row) => row.value === value)?.label ?? value
}

const DevolucionOrderDetail: React.FC<DevolucionOrderDetailProps> = ({ order, children }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    setIsExpanded(false)
  }, [order?.id])

  if (!order) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-gray-500">
        Selecciona o busca un pedido para ver el detalle.
      </div>
    )
  }

  const phone = order.shippingAddress?.phone || order.billingAddress?.phone || ''
  const paymentMethod = order.extraDetails?.payment_method || order.payment_method || ''

  return (
    <div className="space-y-8">
      <div className="rounded-lg bg-white shadow-sm">
        <button
          type="button"
          onClick={() => setIsExpanded((current) => !current)}
          className="flex w-full items-center justify-between gap-3 p-6 text-left"
          aria-expanded={isExpanded}
          aria-controls={`pedido-detalle-${order.id}`}
        >
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-lg font-semibold text-blue-800">
              Pedido #{order.id}
            </h3>
            <span className="rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700">
              {getLabel(ORDER_STATES, order.status)}
            </span>
          </div>
          <ChevronDownIcon
            className={`h-5 w-5 shrink-0 text-blue-800 transition-transform duration-200 ${
              isExpanded ? 'rotate-180' : ''
            }`}
            aria-hidden="true"
          />
        </button>

        {isExpanded && (
          <div id={`pedido-detalle-${order.id}`} className="border-t border-gray-100 px-6 pb-6 pt-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <FormField label="Nombre">
                <Input type="text" value={getDevolucionCustomerName(order)} readOnly mode="view" />
              </FormField>
              <FormField label="Email">
                <Input type="email" value={order.customer?.email || ''} readOnly mode="view" />
              </FormField>
              <FormField label="Teléfono">
                <Input type="text" value={phone} readOnly mode="view" />
              </FormField>
              <FormField label="Tipo de Documento">
                <Input
                  type="text"
                  value={getLabel(tiposDeDocumento, order.customer?.tipo_documento)}
                  readOnly
                  mode="view"
                />
              </FormField>
              <FormField label="Documento">
                <Input type="text" value={order.customer?.document || ''} readOnly mode="view" />
              </FormField>
              <FormField label="Método de Pago">
                <Input
                  type="text"
                  value={getLabel(metodoPago, paymentMethod)}
                  readOnly
                  mode="view"
                />
              </FormField>
            </div>
          </div>
        )}
      </div>

      {/* Direcciones, Envío y Productos del Pedido ocultos temporalmente */}

      {children}
    </div>
  )
}

export default DevolucionOrderDetail
